import {Component, inject, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {AppState} from '../../store/app.state';
import {AsyncPipe, CommonModule, NgIf} from '@angular/common';
import {
    selectUserAgoraVideoCallIsActive,
    selectUserAgoraVideoCallIsJoining,
    selectUserData,
    selectUserHasFeature,
} from '../../store/user/user.selectors';
import {catchError, firstValueFrom, of, switchMap, throwError} from 'rxjs';
import {AppointmentCardComponent} from '../../components/appointment-card/appointment-card.component';
import {TranslocoModule} from '@ngneat/transloco';
import {CoachingSessionService} from '../../services/coaching-session.service';
import {CoachingSession} from '../../models/coaching-session.models';
import {NavigationService} from '../../services/navigation.service';
import {RouterLink} from '@angular/router';
import {APP_ROUTES} from '../../app.routes.definition';
import LoaderComponent from '../../components/loader/loader.component';
import {LogService} from '../../services/log.service';
import {joinAgoraVideoCall, updateAgoraVideoCallIsMinimized} from "../../store/user/user.actions";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ProductFeature} from "../../models/product.model";

@Component({
    selector: 'appointments-page',
    standalone: true,
    imports: [
        AsyncPipe,
        AppointmentCardComponent,
        TranslocoModule,
        CommonModule,
        RouterLink,
        LoaderComponent,
        NgIf,
    ],
    templateUrl: './appointments-page.component.html',
})
export default class AppointmentsPageComponent implements OnInit {
    #store = inject(Store<AppState>);
    #coachingSessionService = inject(CoachingSessionService);
    #navigationService = inject(NavigationService);
    #loggerService = inject(LogService);

    appointments: CoachingSession[] = [];
    currentUser$ = this.#store.select(selectUserData);
    userHasFeatureVideocall$ = this.#store.select(selectUserHasFeature(ProductFeature.VIDEOCALL));

    marketplacePath = APP_ROUTES.MARKETPLACE();
    messagingPath = APP_ROUTES.MESSAGING();

    loading = true;

    constructor() {
        this.currentUser$
            .pipe(
                takeUntilDestroyed(),
                switchMap((user) => {
                    if (user) {
                        return this.#coachingSessionService.getAllCoachingSessions();
                    }
                    return of(null);
                }),
                catchError((error) => {
                    this.#loggerService.error(
                        'Error getting appointments:',
                        error
                    );
                    return throwError(() => error);
                })
            )
            .subscribe({
                next: (response) => {
                    this.appointments = response ?? [];
                    this.loading = false;
                },
                error: (error) => {
                    this.#loggerService.error('Error in subscription:', error);
                    this.loading = false;
                },
            });
    }

    ngOnInit() {
        this.#navigationService.saveCurrentRoute(true);
    }

    async joinVideoMeeting(meetingId: string) {
        const videoCallIsActive = await firstValueFrom(this.#store.select(selectUserAgoraVideoCallIsActive));
        const videoCallIsJoining = await firstValueFrom(this.#store.select(selectUserAgoraVideoCallIsJoining));
        if (videoCallIsActive || videoCallIsJoining) {
            this.#store.dispatch(updateAgoraVideoCallIsMinimized({isMinimized: false}));
        } else {
            this.#store.dispatch(joinAgoraVideoCall({meetingId: meetingId}));
        }
    }
}
